// ----------------------------------------------------------------------
// <copyright file="Izenda.FloatAffixedController.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

!(function() {
  var action = require('mobx').action;
  var extendObservable = require('mobx').extendObservable;
  var AppGlobal = require('Izenda.AppGlobal');
  const { find, isNil } = require('lodash/fp');

  function edgeAlignMaxSpace(amin, amax, pmin, pmax, space) {
    var rspace = space - amax;
    var lspace = amin;
    if (pmax - pmin < space - amin) return amin - pmin;
    return rspace <= lspace ? amax - pmax : amin - pmin;
  }

  function dclamp(delta, pmin, pmax, space) {
    var edgemax = pmax + delta;
    var edgemin = pmin + delta;

    if (edgemin < 0) return delta - edgemin;
    else if (edgemax > space) return delta + (space - edgemax);
    return delta;
  }

  function getEdgeSchemes() {
    return {
      over: {
        name: 'over',
        fits: function(arect, psize, viewport) {
          return psize.y <= Math.min(arect.min.y, viewport.y);
        },
        calcTranslation: function(arect, prect, viewport) {
          return {
            x: dclamp(
              edgeAlignMaxSpace(arect.min.x, arect.max.x, prect.min.x, prect.max.x, viewport.x),
              prect.min.x,
              prect.max.x,
              viewport.x
            ),
            y: dclamp(arect.min.y - prect.max.y, prect.min.y, prect.max.y, viewport.y)
          };
        }
      },
      under: {
        name: 'under',
        fits: function(arect, psize, viewport) {
          return psize.y <= viewport.y - arect.max.y;
        },
        calcTranslation: function(arect, prect, viewport) {
          return {
            x: dclamp(
              edgeAlignMaxSpace(arect.min.x, arect.max.x, prect.min.x, prect.max.x, viewport.x),
              prect.min.x,
              prect.max.x,
              viewport.x
            ),
            y: dclamp(arect.max.y - prect.min.y, prect.min.y, prect.max.y, viewport.y)
          };
        }
      },
      left: {
        name: 'left',
        fits: function(arect, psize, viewport) {
          return psize.x <= Math.min(arect.min.x, viewport.x);
        },
        calcTranslation: function(arect, prect, viewport) {
          return {
            x: dclamp(arect.min.x - prect.max.x, prect.min.x, prect.max.x, viewport.x),
            y: dclamp(
              edgeAlignMaxSpace(arect.min.y, arect.max.y, prect.min.y, prect.max.y, viewport.y),
              prect.min.y,
              prect.max.y,
              viewport.y
            )
          };
        }
      },
      right: {
        name: 'right',
        fits: function(arect, psize, viewport) {
          return psize.x <= viewport.x - arect.max.x;
        },
        calcTranslation: function(arect, prect, viewport) {
          return {
            x: dclamp(arect.max.x - prect.min.x, prect.min.x, prect.max.x, viewport.x),
            y: dclamp(
              edgeAlignMaxSpace(arect.min.y, arect.max.y, prect.min.y, prect.max.y, viewport.y),
              prect.min.y,
              prect.max.y,
              viewport.y
            )
          };
        }
      }
    };
  }

  function inflate(rect) {
    return {
      min: { x: rect.min.x, y: rect.min.y },
      max: { x: rect.max.x, y: rect.max.y }
    };
  }

  function viewportRect(element) {
    var rect = element.getBoundingClientRect();
    return {
      x: rect.left,
      y: rect.top,
      min: { x: rect.left, y: rect.top },
      max: { x: rect.left + rect.width, y: rect.top + rect.height }
    };
  }

  function rectSize(element) {
    var rect = element.getBoundingClientRect();
    return { x: rect.width, y: rect.height };
  }

  function viewportSize() {
    var element = AppGlobal.containerElement.getBoundingClientRect();
    return { x: element.width + element.left, y: element.top + element.height };
  }

  function parseEdgeAlignProps(edges) {
    var schemes = getEdgeSchemes();
    if (!edges) return [schemes.under, schemes.over, schemes.right, schemes.left];

    return edges.split(',').map(function(name) {
      return schemes[name];
    });
  }

  function FloatAffixedController() {
    extendObservable(this, {
      translation: { x: 0, y: 0 },
      schemeName: ''
    });
  }

  FloatAffixedController.prototype.isElementInView = function(element, parentElement) {
    var rect1 = element.getBoundingClientRect();
    var rect2 = parentElement.getBoundingClientRect();
    return rect1.bottom >= rect2.top && rect1.top <= rect2.bottom;
  };

  FloatAffixedController.prototype.chooseScheme = function(arect, psize, viewport) {
    if (this._scheme && this._scheme.fits(arect, psize, viewport) && this._schemes.indexOf(this._scheme) != -1) {
      return this._scheme;
    }

    this._scheme =
      find(function(s) {
        return s.fits(arect, psize, viewport);
      }, this._schemes) || this._schemes[0];

    return this._scheme;
  };

  FloatAffixedController.prototype.reposition = action(function(props, _popup) {
    if (isNil(_popup)) {
      return;
    }
    this._schemes = this._schemes || parseEdgeAlignProps(props.edges);

    var prect = viewportRect(_popup);
    var psize = rectSize(_popup);
    var arect = viewportRect(props.anchor);
    var viewport = viewportSize();

    var scheme = this.chooseScheme(inflate(arect), psize, viewport);
    var delta = scheme.calcTranslation(arect, prect, viewport);

    this.translation = {
      x: this.translation.x + delta.x,
      y: this.translation.y + delta.y
    };
    this.schemeName = scheme.name;
  });

  module.exports = FloatAffixedController;
})();



// WEBPACK FOOTER //
// ./ui/share/components/controls/Izenda.FloatAffixedController.js